import { Component, DestroyRef, Input, Output, EventEmitter, OnChanges, computed, effect, inject, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal, toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { OfferFormComponent } from './offer-form/offer-form.component';
import { ConfirmModalComponent } from '../../component/confirm-modal/confirm-modal.component';
import { PreventionGroupHeaderComponent } from '../../component/prevention-group-header/prevention-group-header.component';
import { ToastService } from '../../core/services/toast.service';
import { lockPageScroll, unlockPageScroll } from '../../layout/shell/shell.component';
import { PreventionCatalogService, MarketCatalogFamily, MarketCatalogBilan, MarketCatalogReferenceOffer } from '../../core/services/prevention-catalog.service';
import {
  PreventionOffersService,
  OrganizationOfferDetail,
  OfferSiteAvailability,
  UpdateOfferRequest,
  CreateOfferRequest,
} from '../../core/services/prevention-offers.service';

/**
 * Résultat de la résolution du template `?fromTemplate=CODE` (Lot 9.0-E.E.E) -- `null` (côté
 * signal) tant que la requête est en cours, jamais confondu avec `{status:'none'}` (aucun
 * `fromTemplate` du tout, cas de la création libre section 16 du lot).
 */
type TemplateResolution =
  | { status: 'none' }
  | { status: 'found'; offer: MarketCatalogReferenceOffer }
  | { status: 'not-found' }
  | { status: 'error' };

/**
 * Page création/édition d'une Offer (Lot 9.0-E.E.D/E.E.E) -- UN seul composant pour les deux routes
 * (`/prevention-offres/nouvelle` et `/prevention-offres/:offerId`), le mode se déduit de la présence
 * du paramètre de route `offerId` (jamais un flag séparé). Orchestration API + navigation
 * uniquement -- la logique de formulaire vit dans {@link OfferFormComponent}. Depuis E.E.E, cette
 * page résout aussi `?fromTemplate=CODE` (bibliothèque Waitora) : `/nouvelle` reste le SEUL
 * mécanisme d'écriture d'une Offer, le template n'est qu'un point de départ optionnel du formulaire.
 */
@Component({
  selector: 'app-offer-form-page',
  standalone: true,
  imports: [TranslatePipe, RouterLink, OfferFormComponent, ConfirmModalComponent, PreventionGroupHeaderComponent],
  templateUrl: './offer-form-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./offer-form-page.component.scss'],
})
export class OfferFormPageComponent implements OnChanges {
  /** `true` quand incrusté ailleurs qu'à sa propre route (ex. sous-page Site, via
   * app-prevention-my-offers embarqué) -- masque le fil d'Ariane propre à cette page (l'hôte a
   * déjà le sien) et lit `offerIdOverride` au lieu du paramètre de route `:offerId` (inexistant
   * hors de sa propre route). `back` remplace la navigation réelle de `goToMyOffers()`. */
  @Input() embedded = false;
  @Input() offerIdOverride: string | null = null;
  /** Code `ReferenceOffer` présélectionné depuis la Bibliothèque Waitora embarquée (remplace le
   * query param `?fromTemplate=` hors de la route propre à ce composant). */
  @Input() fromTemplateOverride: string | null = null;
  /** Consultation seule (ex. "Détails" depuis Site > Prévention > Offres du site sur une offre du
   * catalogue du groupe créée par un autre site) -- jamais un mode de route à part, uniquement
   * pertinent en mode embarqué. Masque Enregistrer/Archiver/photo, désactive tous les champs. */
  @Input() readOnly = false;
  @Output() back = new EventEmitter<void>();
  /** Émis après une création réussie en mode embarqué, à la place de la navigation vers la page
   * dédiée -- l'hôte fait basculer `offerIdOverride` vers l'offre créée pour rester sur place. */
  @Output() created = new EventEmitter<{ offerId: string; name: string }>();

  /** Miroirs signal des 3 @Input ci-dessus. Nécessaire pour DEUX raisons : (1) `offerIdOverride`/
   * `fromTemplateOverride` peuvent changer alors que ce composant reste monté (ex. `onOfferCreated`
   * bascule `offerIdOverride` de null vers l'offre créée SANS démonter/remonter ce composant) ; (2)
   * le suivi de dépendances d'un `computed()` est dynamique par branche -- tant que `embedded` lui
   * -même n'est pas un signal, un changement de `embedded` seul (branche ternaire) n'invaliderait
   * jamais le cache du computed. Un champ `@Input` classique réassigné ne déclenche aucune des deux
   * réévaluations, d'où ce pont via `ngOnChanges`. */
  private readonly embeddedSig = signal(false);
  private readonly offerIdOverrideSig = signal<string | null>(null);
  private readonly fromTemplateOverrideSig = signal<string | null>(null);
  private readonly readOnlyInputSig = signal(false);
  ngOnChanges(): void {
    this.embeddedSig.set(this.embedded);
    this.offerIdOverrideSig.set(this.offerIdOverride);
    this.fromTemplateOverrideSig.set(this.fromTemplateOverride);
    this.readOnlyInputSig.set(this.readOnly);
  }

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly offersSvc = inject(PreventionOffersService);
  private readonly catalogSvc = inject(PreventionCatalogService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly routeOfferId = toSignal(
    this.route.paramMap.pipe(map(params => params.get('offerId'))),
    { initialValue: null as string | null }
  );
  readonly offerId = computed(() => this.embeddedSig() ? this.offerIdOverrideSig() : this.routeOfferId());
  readonly mode = computed<'create' | 'edit'>(() => (this.offerId() ? 'edit' : 'create'));

  readonly loadError = signal(false);
  readonly saving = signal(false);
  readonly serverError = signal<string | null>(null);
  /** Libellé de l'additionalItem visé par un 409 `additional_item_conflicts_with_catalog_bilan`
   *  (`PREVENTION_ADDITIONAL_ITEMS_GUARD_BACKOFFICE_UI`, LOT 3) -- comparaison EXACTE (chaîne
   *  soumise, telle que le backend l'a échoée) uniquement dans {@link OfferFormComponent} pour
   *  surligner le chip correspondant, JAMAIS un re-matching local : le backend reste la seule
   *  source de vérité sur ce qui bloque (section 1/6 de la commande). Remis à `null` à chaque
   *  nouvelle tentative de sauvegarde (voir `onSave`), jamais laissé périmer entre deux essais. */
  readonly lockedAdditionalItem = signal<string | null>(null);

  readonly families = toSignal(
    this.catalogSvc.getFamilies().pipe(catchError(() => { this.loadError.set(true); return of(null); })),
    { initialValue: null as MarketCatalogFamily[] | null }
  );
  readonly bilans = toSignal(
    this.catalogSvc.getBilans().pipe(catchError(() => { this.loadError.set(true); return of(null); })),
    { initialValue: null as MarketCatalogBilan[] | null }
  );

  readonly offerDetail = toSignal(
    toObservable(this.offerId).pipe(
      switchMap(id => id
        ? this.offersSvc.getOffer(id).pipe(catchError(() => { this.loadError.set(true); return of(null); }))
        : of(null)
      )
    ),
    { initialValue: null as OrganizationOfferDetail | null }
  );

  private readonly availability = toSignal(
    toObservable(this.offerId).pipe(
      switchMap(id => id
        ? this.offersSvc.getAvailability(id).pipe(catchError(() => { this.loadError.set(true); return of(null); }))
        : of([] as OfferSiteAvailability[])
      )
    ),
    { initialValue: null as OfferSiteAvailability[] | null }
  );

  /** `?fromTemplate=CODE` (Lot 9.0-E.E.E) -- lu de façon réactive selon les conventions signals du repo. */
  private readonly routeFromTemplateCode = toSignal(
    this.route.queryParamMap.pipe(map(params => params.get('fromTemplate'))),
    { initialValue: null as string | null }
  );
  readonly fromTemplateCode = computed(() => this.embeddedSig() ? this.fromTemplateOverrideSig() : this.routeFromTemplateCode());

  /**
   * UN seul GET /catalog/reference-offers + recherche locale (18 templates, aucun endpoint unitaire
   * justifié -- section 12 du lot). Ne part JAMAIS en requête quand `fromTemplate` est absent (création
   * libre, section 16) -- `{status:'none'}` immédiat via `switchMap`.
   */
  private readonly templateResolution = toSignal<TemplateResolution | null>(
    toObservable(this.fromTemplateCode).pipe(
      switchMap(code => {
        if (!code || this.mode() !== 'create') return of<TemplateResolution>({ status: 'none' });
        return this.catalogSvc.getReferenceOffers().pipe(
          map((offers): TemplateResolution => {
            const found = offers.find(o => o.code === code) ?? null;
            return found ? { status: 'found', offer: found } : { status: 'not-found' };
          }),
          catchError(() => of<TemplateResolution>({ status: 'error' }))
        );
      })
    ),
    { initialValue: null }
  );

  readonly templateLoading = computed(() => this.mode() === 'create' && this.fromTemplateCode() !== null && this.templateResolution() === null);
  /** Vraie erreur réseau (distincte d'un code inconnu, section 18 du lot) -- bloque l'écran plutôt que de préremplir silencieusement un formulaire vide. */
  readonly templateLoadError = computed(() => this.mode() === 'create' && this.templateResolution()?.status === 'error');
  readonly resolvedTemplate = computed<MarketCatalogReferenceOffer | null>(() => {
    const resolution = this.templateResolution();
    return resolution?.status === 'found' ? resolution.offer : null;
  });

  /**
   * Lecture seule : soit explicitement demandée par l'hôte (@Input readOnly, ex. "Détails" d'une
   * offre du catalogue du groupe consultée depuis Site), soit -- hors mode embarqué -- la simple
   * prévisualisation d'un template RÉSOLU de la Bibliothèque Waitora ("Voir l'offre") : un
   * MarketCatalogReferenceOffer n'est "jamais activable directement" (voir
   * prevention-catalog.service.ts) -- seul le bouton dédié "Ajouter à mes offres" de la
   * bibliothèque (POST direct, sans passer par ce formulaire) crée réellement l'Offer, cette page
   * ne fait ici que le PRÉVISUALISER. Basé sur `resolvedTemplate()` (jamais `fromTemplateCode()`
   * brut) : un code invalide/indisponible retombe sur le formulaire de création VIDE, qui doit
   * rester éditable (section 17/18 du lot E.E.E, voir l'effect ci-dessous) -- sinon la page reste
   * verrouillée sans aucun moyen de créer l'offre. En mode embarqué, l'hôte (site.component.ts)
   * reste seul juge via `readOnly` -- jamais cette règle "template" qui ne concerne que la route
   * standalone `/prevention-offres/nouvelle?fromTemplate=`.
   */
  readonly readOnlySig = computed(() =>
    this.readOnlyInputSig() || (this.mode() === 'create' && !this.embeddedSig() && this.resolvedTemplate() !== null)
  );

  constructor() {
    // Section 17 du lot E.E.E : code inconnu ou template désactivé entre le clic et le chargement --
    // ne bloque jamais toute la page, un toast puis le formulaire de création vide (referenceOfferCode
    // reste null, jamais un code invalide conservé dans le POST).
    effect(() => {
      if (this.templateResolution()?.status === 'not-found') {
        this.toast.show(
          this.translate.instant('preventionOffres.offerForm.template.unavailable'),
          'warning',
          3000,
          this.translate.instant('preventionOffres.offerForm.template.unavailableSubtitle')
        );
      }
    });
  }

  readonly loading = computed(() => {
    if (this.families() === null || this.bilans() === null) return true;
    if (this.mode() === 'edit' && (this.offerDetail() === null || this.availability() === null)) return true;
    if (this.templateLoading()) return true;
    return false;
  });

  readonly activeSiteCount = computed(() => this.availability()?.length ?? 0);
  readonly bilanCompositionLocked = computed(() => this.activeSiteCount() > 0 || this.readOnlySig());

  /**
   * Garde "modifications non enregistrées" (audit QA, route standalone uniquement -- le mode
   * embarqué quitte via `back.emit()`, jamais le Router, donc jamais intercepté par un
   * `CanDeactivate`). `readOnlySig()` exclut la prévisualisation d'un modèle : rien n'y est jamais
   * enregistrable, une frappe qui s'y perdrait n'a donc rien d'un oubli.
   */
  readonly formRef = viewChild<OfferFormComponent>('formRef');
  readonly hasUnsavedChanges = computed(() => !this.embedded && !this.readOnlySig() && !!this.formRef()?.isDirty());

  /**
   * Saisie non enregistrée, INDÉPENDAMMENT du mode embarqué (contrairement à `hasUnsavedChanges`,
   * qui reste toujours faux en mode embarqué -- `canDeactivate`/le Router n'y sont jamais sollicités,
   * voir sa Javadoc). L'hôte embarquant (`SiteComponent`) l'utilise pour confirmer AVANT de fermer
   * la sous-page (fix #4, revue technique 2026-09-03) : les sorties du mode incrusté (flèche retour
   * du fil d'Ariane, liens du fil d'Ariane, back du formulaire lui-même) ne passent jamais par le
   * Router et n'étaient donc protégées par aucune garde jusqu'ici.
   */
  readonly isDirty = computed(() => !this.readOnlySig() && !!this.formRef()?.isDirty());
  readonly showLeaveConfirm = signal(false);
  private pendingLeaveResolve: ((allow: boolean) => void) | null = null;

  canDeactivate(): boolean | Promise<boolean> {
    if (!this.hasUnsavedChanges()) return true;
    this.showLeaveConfirm.set(true);
    // Modale déclarée dans CE template, donc DESCENDANTE de #w-main (router-outlet) -- voir le même
    // commentaire sur openArchiveConfirm().
    lockPageScroll({ inertBackground: false });
    return new Promise<boolean>(resolve => { this.pendingLeaveResolve = resolve; });
  }

  confirmLeaveNavigation(): void {
    this.showLeaveConfirm.set(false);
    unlockPageScroll();
    this.pendingLeaveResolve?.(true);
    this.pendingLeaveResolve = null;
  }

  cancelLeaveNavigation(): void {
    this.showLeaveConfirm.set(false);
    unlockPageScroll();
    this.pendingLeaveResolve?.(false);
    this.pendingLeaveResolve = null;
  }

  /** Écrase le nom affiché après un PUT réussi (section 24) -- jamais un rechargement complet. */
  private readonly savedName = signal<string | null>(null);
  readonly displayedOfferName = computed(() => this.savedName() ?? this.offerDetail()?.name ?? '');

  /** Écrase hasPhoto/photoUpdatedAt après upload/suppression réussi -- même principe que `savedName`,
   * jamais un rechargement complet de `offerDetail`. Mêmes signaux/flux que SiteComponent#photoUrl
   * (établissement), adaptés à l'offre : `getPhotoUrl` construit l'URL PUBLIQUE cache-bustée (un
   * `<img src>` n'envoie jamais l'en-tête Authorization). */
  private readonly photoOverride = signal<{ hasPhoto: boolean; photoUpdatedAt: string | null } | undefined>(undefined);
  readonly photoUrl = computed(() => {
    const id = this.offerId();
    if (!id) return null;
    const override = this.photoOverride();
    const detail = this.offerDetail();
    const hasPhoto = override ? override.hasPhoto : (detail?.hasPhoto ?? false);
    const photoUpdatedAt = override ? override.photoUpdatedAt : (detail?.photoUpdatedAt ?? null);
    return hasPhoto ? this.offersSvc.getPhotoUrl(id, photoUpdatedAt) : null;
  });

  readonly uploadingPhoto = signal(false);

  onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    const id = this.offerId();
    if (!file || !id) return;

    this.uploadingPhoto.set(true);
    this.offersSvc.uploadPhoto(id, file).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.uploadingPhoto.set(false);
        this.photoOverride.set({ hasPhoto: true, photoUpdatedAt: new Date().toISOString() });
        this.toast.show(
          this.translate.instant('preventionOffres.offerForm.photo.uploadSuccess'),
          'success',
          3000,
          this.translate.instant('preventionOffres.offerForm.photo.uploadSuccessSubtitle')
        );
      },
      error: (err) => {
        this.uploadingPhoto.set(false);
        const backendMessage = (err as { error?: { message?: string } })?.error?.message;
        if (backendMessage) {
          this.toast.show(backendMessage, 'error');
        } else {
          this.toast.show(
            this.translate.instant('preventionOffres.offerForm.photo.uploadError'),
            'error',
            3000,
            this.translate.instant('preventionOffres.offerForm.photo.uploadErrorSubtitle')
          );
        }
      },
    });
  }

  readonly removePhotoOpen = signal(false);
  readonly removePhotoLoading = signal(false);
  readonly removePhotoError = signal<string | null>(null);

  openRemovePhoto(): void {
    this.removePhotoOpen.set(true);
    this.removePhotoError.set(null);
  }

  closeRemovePhoto(): void {
    if (this.removePhotoLoading()) return;
    this.removePhotoOpen.set(false);
    this.removePhotoError.set(null);
  }

  confirmRemovePhoto(): void {
    const id = this.offerId();
    if (!id) return;
    this.removePhotoLoading.set(true);
    this.removePhotoError.set(null);
    this.offersSvc.deletePhoto(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.photoOverride.set({ hasPhoto: false, photoUpdatedAt: null });
        this.toast.show(
          this.translate.instant('preventionOffres.offerForm.photo.removeSuccess'),
          'success',
          3000,
          this.translate.instant('preventionOffres.offerForm.photo.removeSuccessSubtitle')
        );
        this.removePhotoLoading.set(false);
        this.closeRemovePhoto();
      },
      error: () => {
        this.removePhotoLoading.set(false);
        this.removePhotoError.set(this.translate.instant('preventionOffres.offerForm.photo.removeError'));
      },
    });
  }

  readonly showArchiveConfirm = signal(false);
  readonly archiving = signal(false);
  readonly archiveErrorMessage = signal<string | null>(null);

  readonly archiveConfirmMessage = computed(() => {
    const count = this.activeSiteCount();
    if (count === 0) return this.translate.instant('preventionOffres.myOffers.archive.confirmMessageNoActiveSite');
    const key = count > 1
      ? 'preventionOffres.myOffers.archive.confirmMessageActiveSitesPlural'
      : 'preventionOffres.myOffers.archive.confirmMessageActiveSitesSingular';
    return this.translate.instant(key, { count });
  });

  /**
   * "Retour" du fil d'Ariane -- ramène à la Bibliothèque Waitora quand on est arrivé ici via un
   * modèle (`?fromTemplate=`, ex. "Voir l'offre"), jamais à "Mes Offres" dans ce cas précis (audit
   * QA : perte de contexte de navigation sinon, l'utilisateur venait de la bibliothèque). Basé sur
   * `fromTemplateCode()` brut (pas `resolvedTemplate()`) -- même un code invalide/en cours de
   * résolution signifie qu'on vient bien de la bibliothèque.
   */
  goToMyOffers(): void {
    if (this.embedded) { this.back.emit(); return; }
    if (this.mode() === 'create' && this.fromTemplateCode() !== null) {
      this.router.navigate(['/prevention-offres/bibliotheque']);
      return;
    }
    this.router.navigate(['/prevention-offres']);
  }

  goSites(): void {
    const id = this.offerId();
    if (!id) return;
    this.router.navigate(['/prevention-offres', id, 'sites']);
  }

  onSave(payload: UpdateOfferRequest): void {
    if (this.saving()) return;
    // Fix #6 (revue technique 2026-09-03) : la saisie est considérée "à jour" dès l'intention de
    // sauvegarde exprimée par le clic, jamais seulement au succès HTTP -- sans quoi `canDeactivate`/
    // le mode embarqué (voir `isDirty` ci-dessus) continuent de menacer d'une confirmation "quitter
    // sans enregistrer" pendant que la requête est en vol, pour une saisie que l'utilisateur vient
    // précisément de valider. En cas d'échec, `serverError`/le toast restent affichés (voir
    // `handleSaveError`) donc l'utilisateur n'est jamais laissé dans l'ignorance -- simplement plus
    // jamais nagué une seconde fois s'il choisit malgré tout de quitter après un échec.
    this.formRef()?.markSaved();
    this.saving.set(true);
    this.serverError.set(null);
    this.lockedAdditionalItem.set(null);

    if (this.mode() === 'create') {
      // Provenance uniquement (section 15 du lot E.E.E) -- jamais une instruction serveur pour
      // recopier le template : `payload` porte déjà la composition réellement validée par
      // l'utilisateur, `referenceOfferCode` ne fait que tracer d'où est partie la création.
      const request: CreateOfferRequest = { ...payload, referenceOfferCode: this.resolvedTemplate()?.code ?? null };
      // takeUntilDestroyed (fix #6) : si le composant est détruit pendant que cette requête est en
      // vol (ex. l'utilisateur a confirmé "quitter" sur une AUTRE garde pendant ce court intervalle),
      // le callback ne s'exécute jamais -- plus de navigation fantôme ni de toast après coup.
      this.offersSvc.createOffer(request).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (detail) => {
          this.saving.set(false);
          this.toast.show(
            this.translate.instant('preventionOffres.offerForm.toasts.created'),
            'success',
            3000,
            this.translate.instant('preventionOffres.offerForm.toasts.createdSubtitle')
          );
          if (this.embedded) {
            this.created.emit({ offerId: detail.offerId, name: detail.name });
          } else {
            this.router.navigate(['/prevention-offres', detail.offerId]);
          }
        },
        error: (err) => this.handleSaveError(err),
      });
    } else {
      const id = this.offerId();
      if (!id) return;
      this.offersSvc.updateOffer(id, payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (detail) => {
          this.saving.set(false);
          this.savedName.set(detail.name);
          this.toast.show(
            this.translate.instant('preventionOffres.offerForm.toasts.updated'),
            'success',
            3000,
            this.translate.instant('preventionOffres.offerForm.toasts.updatedSubtitle')
          );
        },
        error: (err) => this.handleSaveError(err),
      });
    }
  }

  private handleSaveError(err: unknown): void {
    this.saving.set(false);
    const errorBody = (err as {
      error?: { code?: string; message?: string; additionalItem?: string; matchedBilanName?: string };
    })?.error;
    const code = errorBody?.code;
    const backendMessage = errorBody?.message;
    let message: string;
    let subtitle: string | undefined;
    if (code === 'offer_composition_locked') {
      message = this.translate.instant('preventionOffres.offerForm.errors.compositionLocked');
      subtitle = this.translate.instant('preventionOffres.offerForm.errors.compositionLockedSubtitle');
    } else if (code === 'additional_item_conflicts_with_catalog_bilan') {
      // LOT 3 (`PREVENTION_ADDITIONAL_ITEMS_GUARD_BACKOFFICE_UI`) -- jamais `recommendationRole`
      // (section 1 : le frontend ne doit jamais s'en servir pour décider quoi que ce soit) ; jamais
      // le mot CATALOG_ONLY ni un code bilan technique dans le message affiché (section 3). Le
      // libellé `matchedBilanName`, quand il est présent, est interpolé tel quel (nom commercial,
      // jamais un code) -- absent (collision multi-bilans côté backend), message générique.
      message = errorBody?.matchedBilanName
        ? this.translate.instant('preventionOffres.offerForm.errors.additionalItemCatalogOnly', {
            bilanName: errorBody.matchedBilanName,
          })
        : this.translate.instant('preventionOffres.offerForm.errors.additionalItemCatalogOnlyGeneric');
      this.lockedAdditionalItem.set(errorBody?.additionalItem ?? null);
    } else if (backendMessage) {
      message = backendMessage;
    } else {
      message = this.translate.instant('preventionOffres.offerForm.errors.saveFailed');
      subtitle = this.translate.instant('preventionOffres.offerForm.errors.saveFailedSubtitle');
    }
    this.serverError.set(message);
    if (subtitle) {
      this.toast.show(message, 'error', 3000, subtitle);
    } else {
      this.toast.show(message, 'error');
    }
  }

  openArchiveConfirm(): void {
    this.archiveErrorMessage.set(null);
    this.showArchiveConfirm.set(true);
    // app-confirm-modal est déclarée dans CE template, donc DESCENDANTE de #w-main (router-outlet)
    // -- inertBackground:false, sinon `inert` sur #w-main rendrait la modale elle-même inerte.
    lockPageScroll({ inertBackground: false });
  }

  closeArchiveConfirm(): void {
    if (this.archiving()) return;
    this.showArchiveConfirm.set(false);
    unlockPageScroll();
  }

  confirmArchive(): void {
    const id = this.offerId();
    if (!id || this.archiving()) return;
    this.archiving.set(true);
    this.offersSvc.archiveOffer(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.archiving.set(false);
        this.showArchiveConfirm.set(false);
        unlockPageScroll();
        // Archivage déjà confirmé via sa propre modale -- jamais redemander via la garde "quitter
        // sans enregistrer" juste après (sinon double confirmation pour une seule intention).
        this.formRef()?.markSaved();
        this.toast.show(
          this.translate.instant('preventionOffres.offerForm.toasts.archived'),
          'success',
          3000,
          this.translate.instant('preventionOffres.offerForm.toasts.archivedSubtitle')
        );
        if (this.embedded) {
          this.back.emit();
        } else {
          this.router.navigate(['/prevention-offres']);
        }
      },
      error: (err) => {
        this.archiving.set(false);
        const message = (err as { error?: { message?: string } })?.error?.message
          ?? this.translate.instant('preventionOffres.offerForm.errors.archiveFailed');
        this.archiveErrorMessage.set(message);
      },
    });
  }
}
